"use client";

import { useState, useRef, useCallback } from 'react';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { FaSpinner, FaChevronRight, FaChevronLeft } from 'react-icons/fa';

interface Vehicle {
  id: string;
  brand: string;
  model: string;
  category: string;
  service_type: string;
}

interface Props {
  initialService?: 'car' | 'bike' | 'household' | 'pet' | '';
  mode?: 'horizontal' | 'vertical';
  variant?: 'light' | 'dark';
  showServiceDropdown?: boolean;
}

const libraries: ("places")[] = ["places"];

const SERVICE_OPTIONS = [
  { value: '', label: 'Select Service' },
  { value: 'car', label: 'Car Transport' },
  { value: 'bike', label: 'Bike Shifting' },
  { value: 'household', label: 'Household Moving' },
  { value: 'pet', label: 'Pet Relocation' },
];

const PET_OPTIONS = ['Dog', 'Cat', 'Other'];

export default function EnquiryForm({
  initialService = '',
  mode = 'vertical',
  variant = 'light',
  showServiceDropdown = true,
}: Props) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
    libraries,
  });

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    service: initialService || '',
    serviceSubtype: '',
    from: '',
    fromLat: 0,
    fromLng: 0,
    to: '',
    toLat: 0,
    toLng: 0,
    name: '',
    mobile: '',
  });

  const [vehicleResults, setVehicleResults] = useState<Vehicle[]>([]);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [isSearchingVehicles, setIsSearchingVehicles] = useState(false);
  const vehicleSearchTimeout = useRef<NodeJS.Timeout | null>(null);

  const fromAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const toAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // --- Place handlers ---
  const onFromPlaceChanged = useCallback(() => {
    if (fromAutocompleteRef.current) {
      const place = fromAutocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setFormData((prev) => ({
          ...prev,
          from: place.formatted_address || place.name || '',
          fromLat: lat,
          fromLng: lng,
        }));
      }
    }
  }, []);

  const onToPlaceChanged = useCallback(() => {
    if (toAutocompleteRef.current) {
      const place = toAutocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setFormData((prev) => ({
          ...prev,
          to: place.formatted_address || place.name || '',
          toLat: lat,
          toLng: lng,
        }));
      }
    }
  }, []);

  // --- Vehicle search ---
  const searchVehicles = useCallback(async (query: string) => {
    if (formData.service !== 'car' && formData.service !== 'bike') {
      setVehicleResults([]);
      setShowVehicleDropdown(false);
      return;
    }
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setVehicleResults([]);
      setShowVehicleDropdown(false);
      return;
    }
    setIsSearchingVehicles(true);
    try {
      const res = await fetch(`/api/vehicles?q=${encodeURIComponent(trimmed)}&service=${formData.service}`);
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setVehicleResults(data);
        setShowVehicleDropdown(data.length > 0);
      } else {
        setVehicleResults([]);
        setShowVehicleDropdown(false);
      }
    } catch (err) {
      console.error('Vehicle search error:', err);
      setVehicleResults([]);
      setShowVehicleDropdown(false);
    } finally {
      setIsSearchingVehicles(false);
    }
  }, [formData.service]);

  const handleVehicleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, serviceSubtype: val }));
    if (vehicleSearchTimeout.current) clearTimeout(vehicleSearchTimeout.current);
    vehicleSearchTimeout.current = setTimeout(() => {
      searchVehicles(val);
    }, 300);
  };

  const handleSelectVehicle = (vehicle: Vehicle) => {
    setFormData((prev) => ({
      ...prev,
      serviceSubtype: `${vehicle.brand} ${vehicle.model}`,
    }));
    setVehicleResults([]);
    setShowVehicleDropdown(false);
  };

  // --- Step navigation ---
  const goToStep2 = () => {
    if (!formData.service) {
      setError('Please select a service.');
      return;
    }
    if (!formData.serviceSubtype.trim()) {
      setError('Please describe your move or enter vehicle model.');
      return;
    }
    if (!formData.from || !formData.to) {
      setError('Please enter both From and To cities.');
      return;
    }
    setError('');
    setStep(2);
  };

  const goToStep1 = () => {
    setStep(1);
  };

  // --- Submit ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile) {
      setError('Name and Mobile are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        from_city: formData.from,
        to_city: formData.to,
        service_type: formData.service,
        service_subtype: formData.serviceSubtype,
        name: formData.name,
        mobile: formData.mobile,
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      };
      const res = await fetch('/api/inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit inquiry');
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Reset ---
  const resetForm = () => {
    setSubmitted(false);
    setError('');
    setStep(1);
    setFormData({
      service: initialService || '',
      serviceSubtype: '',
      from: '',
      fromLat: 0,
      fromLng: 0,
      to: '',
      toLat: 0,
      toLng: 0,
      name: '',
      mobile: '',
    });
    setVehicleResults([]);
    setShowVehicleDropdown(false);
  };

  // --- Input classes – white background, accent focus ring, rounded-md ---
  const inputClass =
    'w-full px-3 py-2.5 rounded-md border border-gray-300 bg-white text-[#343434] placeholder-[#343434]/70 focus:border-[#f62b1e] focus:ring-1 focus:ring-[#f62b1e] focus:ring-opacity-50 outline-none transition';

  const selectClass =
    'w-full px-3 py-2.5 rounded-md border border-gray-300 bg-white text-[#343434] focus:border-[#f62b1e] focus:ring-1 focus:ring-[#f62b1e] focus:ring-opacity-50 outline-none transition';

  // --- Conditional input ---
  const renderConditionalInput = () => {
    const service = formData.service;
    if (service === 'car' || service === 'bike') {
      const placeholder = service === 'car' ? 'Car model (e.g. Creta)' : 'Bike model (e.g. Activa)';
      return (
        <div className="relative flex-1 min-w-[140px]">
          <input
            type="text"
            placeholder={placeholder}
            value={formData.serviceSubtype}
            onChange={handleVehicleInput}
            className={inputClass}
          />
          {isSearchingVehicles && (
            <div className="absolute right-3 top-2.5">
              <FaSpinner className="animate-spin text-[#343434]" />
            </div>
          )}
          {showVehicleDropdown && vehicleResults.length > 0 && (
            <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-md mt-1 shadow-lg max-h-60 overflow-auto">
              {vehicleResults.map((vehicle) => (
                <li
                  key={vehicle.id}
                  onClick={() => handleSelectVehicle(vehicle)}
                  className="px-4 py-2 hover:bg-[#f62b1e]/10 cursor-pointer text-sm text-[#343434]"
                >
                  <span className="font-medium">{vehicle.brand} {vehicle.model}</span>
                  <span className="text-[#343434]/70 text-xs ml-2">({vehicle.category})</span>
                </li>
              ))}
            </ul>
          )}
          {!formData.serviceSubtype && showVehicleDropdown && vehicleResults.length === 0 && (
            <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md mt-1 shadow-lg p-3 text-sm text-[#343434]/70">
              No vehicles found.
            </div>
          )}
        </div>
      );
    } else if (service === 'pet') {
      return (
        <div className="flex-1 min-w-[140px]">
          <select
            value={formData.serviceSubtype}
            onChange={(e) => setFormData((prev) => ({ ...prev, serviceSubtype: e.target.value }))}
            className={selectClass}
          >
            <option value="">Select pet type</option>
            {PET_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    } else {
      const placeholder = service === 'household' ? 'Describe your move (e.g. 2 BHK)' : 'Describe your move (e.g. destination)';
      return (
        <div className="flex-1 min-w-[140px]">
          <input
            type="text"
            placeholder={placeholder}
            value={formData.serviceSubtype}
            onChange={(e) => setFormData((prev) => ({ ...prev, serviceSubtype: e.target.value }))}
            className={inputClass}
          />
        </div>
      );
    }
  };

  // --- Places input ---
  const renderPlacesInput = (placeholder: string, isFrom: boolean) => {
    const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
      if (isFrom) {
        fromAutocompleteRef.current = autocomplete;
      } else {
        toAutocompleteRef.current = autocomplete;
      }
    };

    const onPlaceChanged = isFrom ? onFromPlaceChanged : onToPlaceChanged;
    const value = isFrom ? formData.from : formData.to;
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (isFrom) {
        setFormData((prev) => ({ ...prev, from: val }));
      } else {
        setFormData((prev) => ({ ...prev, to: val }));
      }
    };

    return (
      <div className="flex-1 min-w-[140px]">
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          restrictions={{ country: 'in' }}
        >
          <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className={inputClass}
          />
        </Autocomplete>
      </div>
    );
  };

  // --- Loading / Error ---
  if (loadError) {
    return <div className="p-4 text-red-500 text-sm">Error loading Google Maps</div>;
  }
  if (!isLoaded) {
    return <div className="p-4 text-center text-sm text-[#343434]">Loading maps...</div>;
  }

  // --- Success ---
  if (submitted) {
    return (
      <div className="text-center py-6">
        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-[#292929]">Thank You!</h3>
        <p className="text-sm text-[#343434]">Your inquiry has been received. We'll contact you soon.</p>
        <button
          onClick={resetForm}
          className="mt-4 bg-[#f62b1e] hover:bg-[#df261a] text-white font-semibold py-1.5 px-4 rounded-md text-sm transition"
        >
          Start New Inquiry
        </button>
      </div>
    );
  }

  // --- Horizontal Mode with Two Steps (using grid to prevent layout shift) ---
  if (mode === 'horizontal') {
    return (
      <form onSubmit={step === 2 ? handleSubmit : (e) => e.preventDefault()}>
        {/* Grid container – both steps in the same cell */}
        <div className="grid grid-cols-1">
          {/* Step 1 */}
          <div
            className={`row-start-1 col-start-1 transition-all duration-300 ease-in-out ${
              step === 1
                ? 'opacity-100 translate-x-0 pointer-events-auto'
                : 'opacity-0 -translate-x-4 pointer-events-none'
            }`}
          >
            <div className="flex flex-col sm:flex-row gap-2 items-stretch">
              {showServiceDropdown && (
                <div className="flex-1 min-w-[140px]">
                  <select
                    value={formData.service}
                    onChange={(e) => {
                      const val = e.target.value as typeof formData.service;
                      setFormData((prev) => ({
                        ...prev,
                        service: val,
                        serviceSubtype: '',
                      }));
                      setVehicleResults([]);
                      setShowVehicleDropdown(false);
                    }}
                    className={selectClass}
                  >
                    {SERVICE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {renderConditionalInput()}
              {renderPlacesInput('From City', true)}
              {renderPlacesInput('To City', false)}
              <div className="flex-shrink-0">
                <button
                  type="button"
                  onClick={goToStep2}
                  className={`h-full px-6 py-2.5 bg-[#f62b1e] hover:bg-[#df261a] text-white font-semibold rounded-md transition text-sm shadow-md flex items-center justify-center whitespace-nowrap min-w-[120px] w-full sm:w-auto ${
                    variant === 'dark'
                      ? 'bg-white text-[#f62b1e] hover:bg-[#df261a] hover:text-white'
                      : ''
                  }`}
                >
                  Next <FaChevronRight className="ml-1 text-xs" />
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-500 mt-2 text-left">{error}</p>}
          </div>

          {/* Step 2 */}
          <div
            className={`row-start-1 col-start-1 transition-all duration-300 ease-in-out ${
              step === 2
                ? 'opacity-100 translate-x-0 pointer-events-auto'
                : 'opacity-0 translate-x-4 pointer-events-none'
            }`}
          >
            <div className="flex flex-col sm:flex-row gap-2 items-stretch">
              <div className="flex-1 min-w-[140px]">
                <input
                  type="text"
                  placeholder="Full Name *"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                  className={inputClass}
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <input
                  type="tel"
                  placeholder="Mobile Number *"
                  value={formData.mobile}
                  onChange={(e) => setFormData((prev) => ({ ...prev, mobile: e.target.value }))}
                  required
                  className={inputClass}
                />
              </div>
              <div className="flex-shrink-0 flex gap-2">
                <button
                  type="button"
                  onClick={goToStep1}
                  className="h-full px-4 py-2.5 text-[#343434] hover:text-[#f62b1e] font-semibold rounded-md transition text-sm border border-gray-300 hover:border-[#f62b1e] flex items-center justify-center whitespace-nowrap"
                >
                  <FaChevronLeft className="mr-1 text-xs" /> Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`h-full px-6 py-2.5 bg-[#f62b1e] hover:bg-[#df261a] text-white font-semibold rounded-md transition text-sm shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center whitespace-nowrap min-w-[120px] w-full sm:w-auto ${
                    variant === 'dark'
                      ? 'bg-white text-[#f62b1e] hover:bg-[#df261a] hover:text-white'
                      : ''
                  }`}
                >
                  {loading ? <FaSpinner className="animate-spin" /> : 'Send Inquiry'}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-500 mt-2 text-left">{error}</p>}
          </div>
        </div>
      </form>
    );
  }

  // --- Vertical Mode (fallback) with Two Steps (using grid) ---
  return (
    <form onSubmit={step === 2 ? handleSubmit : (e) => e.preventDefault()} className="space-y-2">
      <div className="grid grid-cols-1">
        {/* Step 1 */}
        <div
          className={`row-start-1 col-start-1 transition-all duration-300 ease-in-out ${
            step === 1
              ? 'opacity-100 translate-x-0 pointer-events-auto'
              : 'opacity-0 -translate-x-4 pointer-events-none'
          }`}
        >
          <div className="space-y-2">
            {showServiceDropdown && (
              <select
                value={formData.service}
                onChange={(e) => {
                  const val = e.target.value as typeof formData.service;
                  setFormData((prev) => ({
                    ...prev,
                    service: val,
                    serviceSubtype: '',
                  }));
                  setVehicleResults([]);
                  setShowVehicleDropdown(false);
                }}
                className={selectClass}
              >
                {SERVICE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            {renderConditionalInput()}
            {renderPlacesInput('Enter pickup city', true)}
            {renderPlacesInput('Enter destination city', false)}
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="button"
              onClick={goToStep2}
              className={`w-full bg-[#f62b1e] hover:bg-[#df261a] text-white font-semibold py-2.5 rounded-md transition text-sm shadow-md flex items-center justify-center gap-2 ${
                variant === 'dark'
                  ? 'bg-white text-[#f62b1e] hover:bg-[#df261a] hover:text-white'
                  : ''
              }`}
            >
              Next <FaChevronRight className="text-xs" />
            </button>
          </div>
        </div>

        {/* Step 2 */}
        <div
          className={`row-start-1 col-start-1 transition-all duration-300 ease-in-out ${
            step === 2
              ? 'opacity-100 translate-x-0 pointer-events-auto'
              : 'opacity-0 translate-x-4 pointer-events-none'
          }`}
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-[#343434]">
              <button
                type="button"
                onClick={goToStep1}
                className="flex items-center gap-1 hover:text-[#f62b1e] transition"
              >
                <FaChevronLeft className="text-xs" /> Back
              </button>
              <span className="text-[10px] text-[#343434]/70">Step 2 of 2</span>
            </div>
            <input
              type="text"
              placeholder="Full Name *"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              required
              className={inputClass}
            />
            <input
              type="tel"
              placeholder="Mobile Number *"
              value={formData.mobile}
              onChange={(e) => setFormData((prev) => ({ ...prev, mobile: e.target.value }))}
              required
              className={inputClass}
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-[#f62b1e] hover:bg-[#df261a] text-white font-semibold py-2.5 rounded-md transition text-sm shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                variant === 'dark'
                  ? 'bg-white text-[#f62b1e] hover:bg-[#df261a] hover:text-white'
                  : ''
              }`}
            >
              {loading ? <FaSpinner className="animate-spin" /> : 'Send Inquiry'}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}